import { FunctionComponent, ReactNode, RefObject } from "react";

import { CskNavCommentCount_feedContentResource$key } from "src/__generated__/relay/CskNavCommentCount_feedContentResource.graphql";
import { MetadataContainer_feedContentResource$key } from "src/__generated__/relay/MetadataContainer_feedContentResource.graphql";
import CskPopupPreview from "src/components/CskPopupPreview";
import CskRevealNav from "src/components/CskRevealNav";
import CskStoryLiveMetadataShell from "src/components/CskStoryLiveMetadataShell";
import CskStoryNavigation from "src/components/CskStoryNavigation";
import DevMessages from "src/components/DevMessages";
import MetadataContainer from "src/components/MetadataContainer";
import SubscribeNavOverlay from "src/components/SubscribeNavOverlay";
import type { DevWarningsNode } from "src/config/devWarnings";
import { vergeGetDrawerComponents } from "src/config/verge-drawer-components";
import { useCskNavCommentCountProps } from "src/hooks/use-csk-nav-comment-count-props";
import {
  resolveStoryNavMode,
  type StoryThemeConfig,
} from "src/utils/story-theme-page-options";

export interface ArticleStoryChromeProps {
  nodeId: string;
  refreshLiveMetadata: boolean;
  feedContentResource: MetadataContainer_feedContentResource$key &
    CskNavCommentCount_feedContentResource$key;
  skipTargetId: string;
  resource: DevWarningsNode;
  getDevWarnings: (node: DevWarningsNode) => string[];
  /** Required with `storyTheme.navMode: "reveal-after-lede"`. */
  revealAfterRef?: RefObject<HTMLElement | null>;
  storyTheme?: StoryThemeConfig;
  children: ReactNode;
}

/**
 * Shared inner chrome for article-style CSK stories: live metadata shell,
 * MetadataContainer, and dev warnings. Stories own the Relay query and block loop.
 */
const ArticleStoryChrome: FunctionComponent<ArticleStoryChromeProps> = ({
  nodeId,
  refreshLiveMetadata,
  feedContentResource,
  skipTargetId,
  resource,
  getDevWarnings,
  revealAfterRef,
  storyTheme,
  children,
}) => {
  const isDev = process.env.NODE_ENV === "development";
  const navMode = storyTheme ? resolveStoryNavMode(storyTheme) : "hidden";
  const commentCountProps = useCskNavCommentCountProps(feedContentResource);
  const fixedNavTheme =
    storyTheme && navMode === "fixed" && storyTheme.minimalNav
      ? storyTheme
      : null;
  const showRevealNav =
    revealAfterRef && storyTheme && navMode === "reveal-after-lede";

  return (
    <CskStoryLiveMetadataShell
      nodeId={nodeId}
      refreshLiveMetadata={refreshLiveMetadata}
    >
      {fixedNavTheme ? (
        <>
          <CskStoryNavigation
            getDrawerComponents={
              fixedNavTheme.drawerSwitcher
                ? vergeGetDrawerComponents
                : undefined
            }
          />
          <SubscribeNavOverlay
            themeEnabled={fixedNavTheme.enabled}
            commentCountProps={commentCountProps}
          />
        </>
      ) : null}
      {showRevealNav ? (
        <CskRevealNav
          revealAfterRef={revealAfterRef}
          themeEnabled={storyTheme.enabled}
          drawerThemeSwitcher={storyTheme.drawerSwitcher}
          commentCountProps={commentCountProps}
        />
      ) : null}
      <MetadataContainer
        refreshLiveMetadata={refreshLiveMetadata}
        feedContentResource={feedContentResource}
      />
      {isDev ? (
        <DevMessages
          resource={resource}
          a11yRootSelector={`#${skipTargetId}`}
          getDevWarnings={getDevWarnings}
        />
      ) : null}
      <CskPopupPreview />
      {children}
    </CskStoryLiveMetadataShell>
  );
};

export default ArticleStoryChrome;
